import { Signal } from 'signal-polyfill';

import { adminApi } from '../../../api/admin.api';
import type {
  FormTemplateCreateRequestDto,
  FormTemplateDetailDto,
  FormTemplateListItemDto,
  FormTemplateUpdateRequestDto,
} from '../../../api/types';
import { authState, canAccessAppAdminConfiguration } from '../../../state/auth.state';
import { BaseElement } from '../../base-element';
import '../../ui/dt-button';
import '../../ui/dt-card';
import '../../ui/dt-dialog';
import '../../ui/dt-input';
import '../../ui/dt-spinner';
import './admin-config-shell';

type PageState = 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden';
type FormMode = 'create' | 'edit' | null;

interface FormTemplateDraft {
  organizationCode: string;
  sportCode: string;
  formCode: string;
  version: string;
  description: string;
}

type FormTemplateField = keyof FormTemplateDraft;
type NormalizedErrorField = FormTemplateField | 'formPdf';

type DtDialogElement = HTMLElement & {
  showDialog(): void;
};

type DtInputElement = HTMLElement & {
  value: string;
  error: string | null;
  validate(): boolean;
};

type DtButtonElement = HTMLElement & {
  loading?: boolean;
  disabled?: boolean;
};

const createFieldConfigs: Array<{
  field: FormTemplateField;
  label: string;
  required?: boolean;
}> = [
  { field: 'organizationCode', label: 'Organization Code', required: true },
  { field: 'sportCode', label: 'Sport Code', required: true },
  { field: 'formCode', label: 'Form Code', required: true },
  { field: 'version', label: 'Version', required: true },
  { field: 'description', label: 'Description' },
];

const uploadErrorCodes = new Set(['INVALID_FILE_TYPE', 'INVALID_PDF_CONTENT', 'FILE_TOO_LARGE']);
const validFormTemplateFields = new Set<NormalizedErrorField>([
  'organizationCode',
  'sportCode',
  'formCode',
  'version',
  'description',
  'formPdf',
]);

function createEmptyDraft(): FormTemplateDraft {
  return {
    organizationCode: '',
    sportCode: '',
    formCode: '',
    version: '',
    description: '',
  };
}

function createDraftFromDetail(detail: FormTemplateDetailDto): FormTemplateDraft {
  return {
    organizationCode: detail.organizationCode ?? '',
    sportCode: detail.sportCode ?? '',
    formCode: detail.formCode ?? '',
    version: detail.version ?? '',
    description: detail.description ?? '',
  };
}

function optionalDescription(value: string): string | null {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function buildCreateRequest(draft: FormTemplateDraft): FormTemplateCreateRequestDto {
  return {
    organizationCode: draft.organizationCode.trim(),
    sportCode: draft.sportCode.trim(),
    formCode: draft.formCode.trim(),
    version: draft.version.trim(),
    description: optionalDescription(draft.description),
  };
}

function buildUpdateRequest(draft: FormTemplateDraft): FormTemplateUpdateRequestDto {
  return {
    description: optionalDescription(draft.description),
  };
}

function normalizeErrorField(fieldName: string): NormalizedErrorField | null {
  const cleaned = fieldName
    .replace(/^\$\.?/, '')
    .replace(/^request\./i, '')
    .replace(/^formTemplate\./i, '');
  const lastSegment = cleaned.split('.').pop() ?? cleaned;
  const normalized = `${lastSegment.charAt(0).toLowerCase()}${lastSegment.slice(1)}`;

  if (validFormTemplateFields.has(normalized as NormalizedErrorField)) {
    return normalized as NormalizedErrorField;
  }

  return null;
}

function formatTemplateKey(
  organizationCode: string,
  sportCode: string,
  formCode: string,
  version: string,
): string {
  return `${organizationCode}/${sportCode}/${formCode}/${version}`;
}

export class AdminFormTemplatesPage extends BaseElement {
  private formTemplates = new Signal.State<FormTemplateListItemDto[]>([]);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private forbidden = new Signal.State(false);
  private formMode = new Signal.State<FormMode>(null);
  private formLoading = new Signal.State(false);
  private formSubmitting = new Signal.State(false);
  private formErrors = new Signal.State<Record<string, string[]>>({});
  private formMessage = new Signal.State<string | null>(null);
  private formDetail = new Signal.State<FormTemplateDetailDto | null>(null);
  private selectedPdf = new Signal.State<File | null>(null);
  private deleteTarget = new Signal.State<{ id: string; label: string } | null>(null);
  private deleteSubmitting = new Signal.State(false);
  private deleteConflictMessage = new Signal.State<string | null>(null);
  private initialized = false;

  private formDraft = createEmptyDraft();
  private editingFormTemplateId: string | null = null;

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();

    if (!auth.isAuthenticated || !canAccessAppAdminConfiguration.get() || this.forbidden.get()) {
      return 'forbidden';
    }

    if (this.loading.get()) {
      return 'loading';
    }

    if (this.error.get()) {
      return 'error';
    }

    if (this.formTemplates.get().length === 0) {
      return 'empty';
    }

    return 'loaded';
  });

  connectedCallback(): void {
    this.watch(this.formTemplates, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.forbidden, () => this.updateRender());
    this.watch(this.formMode, () => this.updateRender());
    this.watch(this.formLoading, () => this.updateRender());
    this.watch(this.formSubmitting, () => this.updateRender());
    this.watch(this.formErrors, () => this.updateRender());
    this.watch(this.formMessage, () => this.updateRender());
    this.watch(this.formDetail, () => this.updateRender());
    this.watch(this.selectedPdf, () => this.updateRender());
    this.watch(this.deleteTarget, () => this.updateRender());
    this.watch(this.deleteSubmitting, () => this.updateRender());
    this.watch(this.deleteConflictMessage, () => this.updateRender());
    this.watch(authState, () => this.ensureLoaded());
    this.watch(canAccessAppAdminConfiguration, () => this.ensureLoaded());

    this.updateRender();
    this.ensureLoaded();
  }

  private canAccess(): boolean {
    const auth = authState.get();
    return auth.isAuthenticated && canAccessAppAdminConfiguration.get();
  }

  private ensureLoaded(): void {
    if (!this.canAccess() || this.initialized) {
      return;
    }

    this.initialized = true;
    void this.loadFormTemplates();
  }

  private async loadFormTemplates(): Promise<void> {
    if (!this.canAccess()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.forbidden.set(false);

    const response = await adminApi.formTemplates.list();
    if (response.error) {
      this.formTemplates.set([]);
      this.loading.set(false);

      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.error.set(response.error.title || 'Unable to load form templates.');
      return;
    }

    this.formTemplates.set(response.data ?? []);
    this.loading.set(false);
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) {
      return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private resetFormState(): void {
    this.formDraft = createEmptyDraft();
    this.editingFormTemplateId = null;
    this.formErrors.set({});
    this.formMessage.set(null);
    this.formLoading.set(false);
    this.formSubmitting.set(false);
    this.formDetail.set(null);
    this.selectedPdf.set(null);
  }

  private openCreateDialog = (): void => {
    this.resetFormState();
    this.formMode.set('create');
  };

  private openEditDialog = async (formTemplateId: string): Promise<void> => {
    this.resetFormState();
    this.formMode.set('edit');
    this.formLoading.set(true);

    const response = await adminApi.formTemplates.getById(formTemplateId);
    this.formLoading.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.formMessage.set(response.error.title || 'Unable to load form template details.');
      return;
    }

    if (!response.data) {
      this.formMessage.set('Unable to load form template details.');
      return;
    }

    this.editingFormTemplateId = String(response.data.formTemplateId);
    this.formDetail.set(response.data);
    this.formDraft = createDraftFromDetail(response.data);
    this.updateRender();
  };

  private closeFormDialog = (): void => {
    this.resetFormState();
    this.formMode.set(null);
  };

  private openDeleteDialog = (formTemplate: FormTemplateListItemDto): void => {
    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(false);
    this.deleteTarget.set({
      id: String(formTemplate.formTemplateId),
      label: formatTemplateKey(
        formTemplate.organizationCode,
        formTemplate.sportCode,
        formTemplate.formCode,
        formTemplate.version,
      ),
    });
  };

  private closeDeleteDialog = (): void => {
    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(false);
    this.deleteTarget.set(null);
  };

  private handleRetry = (): void => {
    void this.loadFormTemplates();
  };

  private handleFieldInput = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const field = target.getAttribute('data-field') as FormTemplateField | null;
    const detail = (event as CustomEvent<{ value: string }>).detail;
    if (!field) {
      return;
    }

    this.formDraft = {
      ...this.formDraft,
      [field]: detail?.value ?? '',
    };

    const nextErrors = { ...this.formErrors.get() };
    delete nextErrors[field];
    this.formErrors.set(nextErrors);

    if (this.formMessage.get() === 'Please correct the highlighted fields and try again.') {
      this.formMessage.set(null);
    }
  };

  private handlePdfChange = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    const file = target.files?.[0] ?? null;
    this.selectedPdf.set(file);

    const nextErrors = { ...this.formErrors.get() };
    delete nextErrors.formPdf;
    this.formErrors.set(nextErrors);

    if (this.formMessage.get() === 'Review the PDF upload and try again.') {
      this.formMessage.set(null);
    }
  };

  private validateForm(): boolean {
    if (this.formMode.get() !== 'create') {
      return true;
    }

    return ['organizationCode', 'sportCode', 'formCode', 'version'].every((fieldName) => {
      const input = this.$(`[data-field="${fieldName}"]`) as DtInputElement | null;
      return input ? input.validate() : true;
    });
  }

  private applyServerErrors(errors?: Record<string, string[]>): void {
    if (!errors) {
      this.formErrors.set({});
      return;
    }

    const normalizedErrors: Record<string, string[]> = {};
    Object.entries(errors).forEach(([field, messages]) => {
      const normalizedField = normalizeErrorField(field);
      if (!normalizedField) {
        return;
      }

      normalizedErrors[normalizedField] = messages;
    });

    this.formErrors.set(normalizedErrors);
  }

  private async handleSave(): Promise<void> {
    const mode = this.formMode.get();
    if (!mode) {
      return;
    }

    this.formMessage.set(null);
    this.formErrors.set({});

    if (!this.validateForm()) {
      this.formMessage.set('Please correct the highlighted fields and try again.');
      return;
    }

    this.formSubmitting.set(true);

    const response = mode === 'create'
      ? await adminApi.formTemplates.create(
          buildCreateRequest(this.formDraft),
          this.selectedPdf.get(),
        )
      : await adminApi.formTemplates.update(
          this.editingFormTemplateId ?? '',
          buildUpdateRequest(this.formDraft),
          this.selectedPdf.get(),
        );

    this.formSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.applyServerErrors(response.error.errors);

      if (response.error.status === 409 && response.error.errorCode === 'DUPLICATE_KEY') {
        this.formMessage.set(
          'A form template with that organization, sport, form code, and version already exists.'
        );
        return;
      }

      const hasUploadError = Boolean(response.error.errors?.formPdf)
        || uploadErrorCodes.has(response.error.errorCode ?? '');

      this.formMessage.set(
        hasUploadError
          ? 'Review the PDF upload and try again.'
          : response.error.errors
            ? 'Please correct the highlighted fields and try again.'
            : response.error.title || 'Unable to save form template.'
      );
      return;
    }

    this.closeFormDialog();
    await this.loadFormTemplates();
  }

  private async handleDelete(): Promise<void> {
    const target = this.deleteTarget.get();
    if (!target) {
      return;
    }

    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(true);

    const response = await adminApi.formTemplates.delete(target.id);

    this.deleteSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      if (response.error.status === 409 && response.error.errorCode === 'REFERENCED') {
        this.deleteConflictMessage.set(
          'This form template is already referenced by one or more trial events. Update those trial events before deleting the template.'
        );
        return;
      }

      this.deleteConflictMessage.set(response.error.title || 'Unable to delete form template.');
      return;
    }

    this.closeDeleteDialog();
    await this.loadFormTemplates();
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-[24px] border border-neutral-200 bg-white" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading form templates...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="rounded-[24px] border border-error-200 bg-error-50 p-6" data-testid="error-state">
        <h2 class="text-lg font-semibold text-error-700">Unable to load form templates</h2>
        <p class="mt-2 text-sm text-error-600">${this.esc(message)}</p>
        <dt-button class="mt-4" variant="outline" data-testid="retry-btn">Retry</dt-button>
      </div>
    `;
  }

  private renderEmpty(): string {
    return `
      <div class="rounded-[24px] border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="empty-state">
        <div class="text-lg font-semibold text-neutral-900">No form templates yet</div>
        <p class="mt-2 text-sm text-neutral-600">Create the first organization, sport, form, and version key used by trial-event setup.</p>
        <dt-button class="mt-5" data-testid="create-empty-btn">Create form template</dt-button>
      </div>
    `;
  }

  private renderTable(): string {
    return `
      <dt-card variant="outlined">
        <div slot="header">Form Templates</div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div class="max-w-2xl text-sm text-neutral-600">
              Manage published form-template identity keys, description copy, and optional PDF assets through the existing multipart admin contract.
            </div>
            <dt-button data-testid="create-btn">Create form template</dt-button>
          </div>

          <div class="overflow-hidden rounded-[24px] border border-neutral-200 bg-white" data-testid="form-templates-table">
            <div class="grid grid-cols-1 gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 lg:grid-cols-[1.5fr_1.8fr_0.9fr_0.9fr_1fr] lg:gap-4">
              <div>Template Key</div>
              <div>Description</div>
              <div>PDF</div>
              <div>Created</div>
              <div>Actions</div>
            </div>

            <div class="divide-y divide-neutral-200">
              ${this.formTemplates
                .get()
                .map(
                  (formTemplate) => `
                    <div class="grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[1.5fr_1.8fr_0.9fr_0.9fr_1fr] lg:items-center" data-testid="form-template-row" data-form-template-id="${this.esc(String(formTemplate.formTemplateId))}">
                      <div class="space-y-1 text-sm text-neutral-700">
                        <div class="font-semibold text-neutral-900">${this.esc(
                          formatTemplateKey(
                            formTemplate.organizationCode,
                            formTemplate.sportCode,
                            formTemplate.formCode,
                            formTemplate.version,
                          ),
                        )}</div>
                        <div class="text-xs text-neutral-500">${this.esc(formTemplate.organizationCode)} · ${this.esc(formTemplate.sportCode)} · ${this.esc(formTemplate.formCode)}</div>
                      </div>
                      <div class="text-sm text-neutral-700">${this.esc(formTemplate.description ?? 'No description')}</div>
                      <div>
                        <span class="inline-flex rounded-full px-3 py-1 text-xs font-semibold ${formTemplate.hasPdf ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-600'}" data-testid="pdf-presence-badge">
                          ${formTemplate.hasPdf ? 'PDF attached' : 'No PDF'}
                        </span>
                      </div>
                      <div class="text-sm text-neutral-600">${this.esc(this.formatTimestamp(formTemplate.createdAtUtc))}</div>
                      <div class="flex flex-wrap items-center gap-2">
                        <button class="rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50" data-testid="edit-btn" data-form-template-id="${this.esc(String(formTemplate.formTemplateId))}">Edit</button>
                        <button class="rounded-full border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50" data-testid="delete-btn" data-form-template-id="${this.esc(String(formTemplate.formTemplateId))}">Delete</button>
                      </div>
                    </div>
                  `,
                )
                .join('')}
            </div>
          </div>
        </div>
      </dt-card>
    `;
  }

  private renderFormDialogBody(): string {
    const mode = this.formMode.get();
    const detail = this.formDetail.get();

    if (this.formLoading.get()) {
      return `
        <div class="flex min-h-[220px] flex-col items-center justify-center gap-3">
          <dt-spinner size="large"></dt-spinner>
          <p class="text-sm text-neutral-600">Loading form template details...</p>
        </div>
      `;
    }

    if (mode === 'edit' && !detail) {
      return `
        <div class="rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700">
          Unable to load form template details. Close the dialog and try again.
        </div>
      `;
    }

    if (mode === 'create') {
      return `
        <div class="grid gap-4 md:grid-cols-2">
          ${createFieldConfigs
            .map(
              (fieldConfig) => `
                <dt-input
                  label="${fieldConfig.label}"
                  ${fieldConfig.required ? 'required' : ''}
                  data-field="${fieldConfig.field}"
                ></dt-input>
              `,
            )
            .join('')}
        </div>
      `;
    }

    return `
      <div class="space-y-5">
        <div class="rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="template-identity-summary">
          <div class="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">Template Key</div>
          <div class="mt-2 text-sm font-semibold text-neutral-900">${this.esc(
            formatTemplateKey(
              detail?.organizationCode ?? '',
              detail?.sportCode ?? '',
              detail?.formCode ?? '',
              detail?.version ?? '',
            ),
          )}</div>
          <div class="mt-1 text-sm text-neutral-600">Only the description and optional PDF replacement are editable after create.</div>
        </div>
        <dt-input label="Description" data-field="description"></dt-input>
      </div>
    `;
  }

  private renderPdfUploadPanel(): string {
    const detail = this.formDetail.get();
    const currentHasPdf = Boolean(detail?.formPdfUrl);
    const pdfError = this.formErrors.get().formPdf?.[0] ?? null;
    const selectedFile = this.selectedPdf.get();
    const isCreate = this.formMode.get() === 'create';

    return `
      <div class="rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="pdf-upload-panel">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div class="space-y-1">
            <div class="text-sm font-semibold text-neutral-900">${isCreate ? 'Optional PDF upload' : 'Current PDF and replacement'}</div>
            <div class="text-sm text-neutral-600" data-testid="current-pdf-status">${currentHasPdf ? 'PDF attached' : 'No PDF attached'}</div>
            ${detail?.formPdfUrl ? `<a href="${this.esc(detail.formPdfUrl)}" class="text-sm text-primary-600 underline" target="_blank" rel="noreferrer" data-testid="current-pdf-link">Open current PDF</a>` : ''}
          </div>
          <label class="flex flex-col gap-1 text-sm font-medium text-neutral-700">
            <span>${isCreate ? 'Attach PDF' : 'Replace PDF'}</span>
            <input type="file" accept="application/pdf" class="text-sm text-neutral-700" data-testid="form-pdf-input" ${this.formSubmitting.get() || this.formLoading.get() ? 'disabled' : ''} />
            ${pdfError ? `<span class="text-xs text-error-600">${this.esc(pdfError)}</span>` : ''}
          </label>
        </div>
        ${selectedFile ? `<div class="mt-3 text-xs text-neutral-600" data-testid="selected-pdf-name">Selected file: ${this.esc(selectedFile.name)}</div>` : ''}
      </div>
    `;
  }

  private renderFormDialog(): string {
    const mode = this.formMode.get();
    if (!mode) {
      return '';
    }

    const isCreate = mode === 'create';
    const title = isCreate ? 'Create form template' : 'Edit form template';

    return `
      <dt-dialog data-testid="form-template-form-dialog">
        <div class="space-y-6">
          <div>
            <h2 class="text-2xl font-semibold text-neutral-950">${title}</h2>
            <p class="mt-2 text-sm text-neutral-600">Use the published multipart admin contract to manage the template identity fields and optional PDF upload.</p>
          </div>

          ${
            this.formMessage.get()
              ? `<div class="rounded-2xl border border-error-200 bg-error-50 p-4 text-sm text-error-700" data-testid="form-message">${this.esc(this.formMessage.get() ?? '')}</div>`
              : ''
          }

          ${this.renderFormDialogBody()}
          ${this.renderPdfUploadPanel()}

          <div class="flex flex-wrap items-center justify-end gap-3">
            <dt-button variant="ghost" data-testid="cancel-form-btn">Cancel</dt-button>
            <dt-button data-testid="save-form-btn">${isCreate ? 'Create form template' : 'Save changes'}</dt-button>
          </div>
        </div>
      </dt-dialog>
    `;
  }

  private renderDeleteDialog(): string {
    const target = this.deleteTarget.get();
    if (!target) {
      return '';
    }

    return `
      <dt-dialog data-testid="delete-dialog">
        <div class="space-y-6">
          <div>
            <h2 class="text-2xl font-semibold text-neutral-950">Delete form template</h2>
            <p class="mt-2 text-sm text-neutral-600">This removes <span class="font-semibold text-neutral-900">${this.esc(target.label)}</span> only when no existing trial event still references it.</p>
          </div>

          ${
            this.deleteConflictMessage.get()
              ? `<div class="rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700" data-testid="delete-conflict-message">${this.esc(this.deleteConflictMessage.get() ?? '')}</div>`
              : ''
          }

          <div class="flex flex-wrap items-center justify-end gap-3">
            <dt-button variant="ghost" data-testid="cancel-delete-btn">Cancel</dt-button>
            <dt-button variant="outline" data-testid="confirm-delete-btn">Delete form template</dt-button>
          </div>
        </div>
      </dt-dialog>
    `;
  }

  private renderPageBody(): string {
    const state = this.pageState.get();
    if (state === 'loading') {
      return this.renderLoading();
    }

    if (state === 'error') {
      return this.renderError(this.error.get() ?? 'Unable to load form templates.');
    }

    if (state === 'empty') {
      return this.renderEmpty();
    }

    return this.renderTable();
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl text-center">
          <h2 class="text-xl font-semibold text-neutral-900">Access restricted</h2>
          <p class="mt-2 text-sm text-neutral-600">Only AppAdmin users can access the configuration area.</p>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    if (this.pageState.get() === 'forbidden') {
      this.render(this.renderForbidden());
      return;
    }

    this.render(`
      <dt-admin-config-shell
        active-section="form-templates"
        section-title="Form Templates"
        section-description="Manage published form-template keys, descriptions, and optional PDF assets used by the shared trial-event setup workflow."
        sport-codes-path="/admin/configuration/sport-codes"
      >
        ${this.renderPageBody()}
        ${this.renderFormDialog()}
        ${this.renderDeleteDialog()}
      </dt-admin-config-shell>
    `);

    this.attachEventListeners();
    this.assignData();
    this.syncDialogs();
  }

  private attachEventListeners(): void {
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.handleRetry);
    }

    const createBtn = this.$('[data-testid="create-btn"]');
    if (createBtn) {
      createBtn.addEventListener('click', this.openCreateDialog);
    }

    const createEmptyBtn = this.$('[data-testid="create-empty-btn"]');
    if (createEmptyBtn) {
      createEmptyBtn.addEventListener('click', this.openCreateDialog);
    }

    this.$$('[data-testid="edit-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const formTemplateId = button.getAttribute('data-form-template-id');
        if (formTemplateId) {
          void this.openEditDialog(formTemplateId);
        }
      });
    });

    this.$$('[data-testid="delete-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const formTemplateId = button.getAttribute('data-form-template-id');
        const formTemplate = this.formTemplates
          .get()
          .find((item) => String(item.formTemplateId) === formTemplateId);
        if (formTemplate) {
          this.openDeleteDialog(formTemplate);
        }
      });
    });

    const cancelFormBtn = this.$('[data-testid="cancel-form-btn"]');
    if (cancelFormBtn) {
      cancelFormBtn.addEventListener('click', this.closeFormDialog);
    }

    const saveFormBtn = this.$('[data-testid="save-form-btn"]');
    if (saveFormBtn) {
      saveFormBtn.addEventListener('click', () => {
        void this.handleSave();
      });
    }

    const formPdfInput = this.$('[data-testid="form-pdf-input"]');
    if (formPdfInput) {
      formPdfInput.addEventListener('change', this.handlePdfChange);
    }

    const cancelDeleteBtn = this.$('[data-testid="cancel-delete-btn"]');
    if (cancelDeleteBtn) {
      cancelDeleteBtn.addEventListener('click', this.closeDeleteDialog);
    }

    const confirmDeleteBtn = this.$('[data-testid="confirm-delete-btn"]');
    if (confirmDeleteBtn) {
      confirmDeleteBtn.addEventListener('click', () => {
        void this.handleDelete();
      });
    }

    this.$$('dt-input[data-field]').forEach((input) => {
      input.addEventListener('input', this.handleFieldInput);
    });

    const formDialog = this.$('[data-testid="form-template-form-dialog"]');
    if (formDialog) {
      formDialog.addEventListener('close', this.closeFormDialog);
    }

    const deleteDialog = this.$('[data-testid="delete-dialog"]');
    if (deleteDialog) {
      deleteDialog.addEventListener('close', this.closeDeleteDialog);
    }
  }

  private assignData(): void {
    if (this.formMode.get()) {
      this.$$('dt-input[data-field]').forEach((input) => {
        const field = input.getAttribute('data-field') as FormTemplateField | null;
        if (!field) {
          return;
        }

        const dtInput = input as DtInputElement;
        dtInput.value = this.formDraft[field];
        dtInput.error = this.formErrors.get()[field]?.[0] ?? null;
      });

      const saveBtn = this.$('[data-testid="save-form-btn"]') as DtButtonElement | null;
      if (saveBtn) {
        const disableForMissingEditDetail = this.formMode.get() === 'edit' && !this.formDetail.get();
        saveBtn.loading = this.formSubmitting.get();
        saveBtn.disabled = this.formLoading.get() || this.formSubmitting.get() || disableForMissingEditDetail;
      }
    }

    const confirmDeleteBtn = this.$('[data-testid="confirm-delete-btn"]') as DtButtonElement | null;
    if (confirmDeleteBtn) {
      confirmDeleteBtn.loading = this.deleteSubmitting.get();
      confirmDeleteBtn.disabled = this.deleteSubmitting.get();
    }
  }

  private syncDialogs(): void {
    const formDialog = this.$('[data-testid="form-template-form-dialog"]') as DtDialogElement | null;
    if (formDialog) {
      formDialog.showDialog();
    }

    const deleteDialog = this.$('[data-testid="delete-dialog"]') as DtDialogElement | null;
    if (deleteDialog) {
      deleteDialog.showDialog();
    }
  }
}

customElements.define('dt-admin-form-templates-page', AdminFormTemplatesPage);